import moment from "moment";
import { reformatDateForServer } from "@/helpers/workWithDates";
import { ViolationTypeDescription } from "@/entities/models/violation-type";

export const formatRecordForTable = (record) => {
    const isExplanatoryRequired = record.explanatory_required.toString() === "1";

    return {
        id: record.id,
        doesRequireExplanatory: isExplanatoryRequired,
        explanatory: {
            entity: record.explanatory,
            isReviewed: record.explanatory ? record.explanatory.is_viewed === 1 : false,
        },
        isShowRow: true,
        fio: {
            value: record.worker_name,
            violationFullNameCell: {
                workerName: record.worker_name,
                workerId: record.worker_id,
                isImportantFoul: record.foulType && record.foulType.is_important,
                isRedCell: isExplanatoryRequired && !record.explanatory,
                isBlueCell: isExplanatoryRequired && record.explanatory && record.explanatory.is_viewed === 0,
            },
        },
        post: {
            value: record.positions.map((p) => p.name).join("\n"),
        },
        filial: {
            value: record.filial_name,
            id: record.filial_id,
        },
        date: {
            value: moment(record.date).format("DD.MM.YYYY"),
        },
        foul: (() => {
            const { foulType, foul_id } = record;

            const model = foulType && ViolationTypeDescription(foulType);

            if (model && model.hasDescription) {
                return {
                    value: foulType && foulType.description,
                    id: foul_id,
                    descriptionModel: model,
                    class: "with-description",
                    onClick: "showDescription",
                };
            }

            return {
                value: foulType && foulType.description,
                id: foul_id,
            };
        })(),

        points: {
            value: record.points,
        },
        addedBy: {
            value: record.added_by,
        },
        deadline: {
            violationDeadlineCell: {
                deadline: record.deadline ? moment(record.deadline).format("DD.MM.YYYY") : "",
                isFined: record.lack_of_explanatory === 1,
                id: record.id,
            },
        },
    };
};

const tableSortingValues = {
    fio: "worker.name",
    date: "date",
    addedBy: "added_by",
    deadline: "deadline",
    points: "foul_types.points",
    foul: "foul_types.description",
    filial: "filial.filial_name",
    post: "post.name",
};

const columnStringFiltersValues = {
    fio: {
        1: "worker_name",
        2: "worker_like",
    },
    foul: {
        1: "foul_name",
        2: "foul_like",
    },
    addedBy: {
        1: "added_name",
        2: "added_like",
    },
};

const columnNumberFilterValues = {
    1: (value) => `<${value}`,
    2: (value) => `>${value}`,
    3: (value) => value,
    4: ({ start, end }) => `${start}-${end}`,
};

const columnDateFilterValues = {
    date: {
        date: (date) => `start=${reformatDateForServer(date)}&finish=${reformatDateForServer(date)}`,
        before: (date) => `finish=${reformatDateForServer(date)}`,
        after: (date) => `start=${reformatDateForServer(date)}`,
        range: (date) => {
            const [start, end] = date.split(",");
            return `start=${reformatDateForServer(start)}&finish=${reformatDateForServer(end)}`;
        },
    },
    deadline: {
        date: (date) => `deadline_start=${reformatDateForServer(date)}&deadline_finish=${reformatDateForServer(date)}`,
        before: (date) => `deadline_finish=${reformatDateForServer(date)}`,
        after: (date) => `deadline_start=${reformatDateForServer(date)}`,
        range: (date) => {
            const [start, end] = date.split(",");
            return `deadline_start=${reformatDateForServer(start)}&deadline_finish=${reformatDateForServer(end)}`;
        },
    },
};

const columnListFilterValues = {
    post: {
        1: (values) => `post_codes=${values.map((v) => v).join(",")}`,
        2: (values) => `post_codes=${values.map((v) => `-${v}`).join(",")}`,
    },
    filial: {
        1: (values) => `filial_ids=${values.map((v) => v).join(",")}`,
        2: (values) => `filial_ids=${values.map((v) => `-${v}`).join(",")}`,
    },
};

const isPointsRangeFilterValidated = (pointsRange) => {
    const [start, end] = pointsRange;
    let isValid = true;
    if (start === 0 && end === 0) {
        isValid = false;
    }
    if (start === 100 && end === 100) {
        isValid = false;
    }
    if (start === 0 && end === 100) {
        isValid = false;
    }
    return isValid;
};

const getQueriedParamsFromColumnFilters = (columnFilters) => {
    const queriedFilters = [];

    const entries = Object.entries(columnFilters);
    entries.forEach(([filterKey, filterValue]) => {
        if (filterValue.type === "string") {
            const query = columnStringFiltersValues[filterKey][filterValue.searchType];
            queriedFilters.push(`${query}=${filterValue.value}`);
        }
        if (filterValue.type === "number") {
            queriedFilters.push(`${filterKey}=${columnNumberFilterValues[filterValue.searchType](filterValue.value)}`);
        }
        if (filterValue.type === "date") {
            queriedFilters.push(columnDateFilterValues[filterKey][filterValue.searchType](filterValue.value));
        }
        if (filterValue.type === "list") {
            queriedFilters.push(
                columnListFilterValues[filterKey][filterValue.searchType](filterValue.value.split(",")),
            );
        }
    });

    return queriedFilters;
};

export const getQueryParamsForFetchWorkers = (filters) => {
    let queryParams = [`page=${filters.page}`];
    const {
        filials,
        positions,
        workers,
        violationTypes,
        explanationExistence,
        pointsRange,
        filialsExcept,
        positionsExcept,
        violationTypesExcept,
        sortBy,
        sortType,
        period,
        columnFilters,
    } = filters;

    if (filials && filials.length) {
        const filialIds = filials
            .map((filial) => {
                if (filialsExcept) {
                    return `-${filial.value}`;
                }
                return filial.value;
            })
            .join(",");
        queryParams.push(`filial_ids=${filialIds}`);
    }
    if (positions && positions.length) {
        const positionCodes = positions
            .map((position) => {
                if (positionsExcept) {
                    return `-${position.value}`;
                }
                return position.value;
            })
            .join(",");
        queryParams.push(`post_codes=${positionCodes}`);
    }
    if (workers && workers.length) {
        const workerIds = workers.map((worker) => worker.value).join(",");
        queryParams.push(`worker_ids=${workerIds}`);
    }
    if (violationTypes && violationTypes.length) {
        const violationIds = violationTypes
            .map((violation) => {
                if (violationTypesExcept) {
                    return `-${violation.value}`;
                }
                return violation.value;
            })
            .join(",");
        queryParams.push(`foul_ids=${violationIds}`);
    }
    if (pointsRange && pointsRange.length) {
        if (isPointsRangeFilterValidated(pointsRange)) {
            const [start, end] = pointsRange;
            queryParams.push(`points=${start}-${end}`);
        }
    }
    if (sortBy && sortType) {
        queryParams.push(`sort=${sortType === "desc" ? "-" : ""}${tableSortingValues[sortBy]}`);
    }
    if (period) {
        const [startDate, endDate] = period.split("-").map((date) => (date.trim() ? reformatDateForServer(date) : ""));
        if (startDate && endDate) {
            queryParams.push(`start=${startDate}&finish=${endDate}`);
        }
    }
    if (explanationExistence) {
        queryParams.push(`explanatory=${explanationExistence.value}`);
    }

    if (columnFilters && Object.keys(columnFilters).length) {
        const queriedParams = getQueriedParamsFromColumnFilters(columnFilters);
        queryParams = queryParams.concat(queriedParams);
    }

    return queryParams;
};
